import { Link } from "react-router-dom"
import { addDays, eachDayOfInterval, endOfMonth, endOfWeek, format, isBefore, isSameMonth, isToday, isWeekend, parseISO, startOfDay, startOfMonth, startOfWeek } from "date-fns"
import { cn } from "@/lib/utils"
import { Plus, Sparkles, StickyNote } from "lucide-react"
import { HALF_COLS, WEEK_OPTS, cleanSegments, cleanerFirstName, isBlock, isPast, isResidency, propertyColor, weekSegments } from "~/lib/calendar"
import routes from "~/routes"

const WEEKDAYS = [ "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun" ]

const STRIP_STYLES = {
  unassigned: "border border-dashed border-stone-400 bg-stone-100 text-stone-600 dark:border-stone-500 dark:bg-stone-800 dark:text-stone-300",
  pending: "bg-amber-200 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100",
  accepted: "bg-emerald-200 text-emerald-900 dark:bg-emerald-900/60 dark:text-emerald-100",
  done: "bg-muted text-muted-foreground",
  flagged: "bg-red-200 text-red-900 dark:bg-red-900/60 dark:text-red-100",
}

// A clean runs under its property's bookings as a thin strip covering the
// day(s) it can happen -- two days for a changeover -- with the date and the
// cleaner, or "no cleaner yet" in a dashed grey.
function CleanStrip({ segment }) {
  const { clean, col, span, row, startsHere, endsHere } = segment
  const status = clean.flagged_reason ? "flagged" : !clean.cleaner_id ? "unassigned" : clean.status
  const when = clean.kind === "changeover" || clean.kind === "cleaner_chooses"
    ? `${format(parseISO(clean.window_starts_on), "EEE d")}–${format(parseISO(clean.window_ends_on), "EEE d")}${clean.kind === "cleaner_chooses" ? " · cleaner picks" : ""}`
    : `${format(parseISO(clean.window_starts_on), "EEE d")}${clean.kind === "back_to_back" ? " 11–3" : clean.scheduled_time ? ` ${clean.scheduled_time}` : ""}`
  const who = clean.cleaner_id ? cleanerFirstName(clean) : "no cleaner yet"

  return (
    <Link
      to={`${routes.cleans}?highlight=${clean.id}`}
      style={{ gridColumn: `${col + 1} / span ${span}`, gridRow: row + 1 }}
      title={`Clean ${clean.when_label || when} — ${clean.cleaner_name || "unassigned"}${clean.flagged_reason ? ` (${clean.flagged_reason})` : ""}`}
      className={cn(
        "flex h-5 items-center gap-1 truncate px-1.5 text-[10px] font-semibold leading-none hover:opacity-80",
        STRIP_STYLES[status] || STRIP_STYLES.done,
        startsHere && "ml-1.5 rounded-l",
        endsHere && "mr-1.5 rounded-r",
      )}
    >
      <Sparkles size={9} className="shrink-0" />
      <span className="truncate">{when} · {who}</span>
    </Link>
  )
}

function BookingBar({ segment, property }) {
  const { booking, col, span, row, startsHere, endsHere } = segment
  const block = isBlock(booking)
  const residency = isResidency(booking)
  const past = isPast(booking)
  const label = block ? (booking.notes ? `Blocked · ${booking.notes}` : "Blocked") : residency ? `Residency · ${booking.guest_label}` : booking.guest_label

  return (
    <Link
      to={routes.bookingDetail(booking.id)}
      style={{
        gridColumn: `${col + 1} / span ${span}`,
        gridRow: row + 1,
        // Residencies keep the property colour but wear diagonal stripes so
        // they read differently from paying stays at a glance.
        background: block
          ? undefined
          : residency
            ? `repeating-linear-gradient(135deg, ${propertyColor(property)} 0 8px, rgba(255,255,255,0.28) 8px 12px)`
            : propertyColor(property),
      }}
      title={`${property.name}: ${label}${booking.needs_enrichment ? " (needs guest details)" : ""}`}
      className={cn(
        "mt-0.5 flex h-7 items-center gap-1 truncate px-2 text-xs font-medium hover:opacity-80",
        block ? "bg-stone-200 text-stone-500 dark:bg-stone-700 dark:text-stone-300" : "text-white [text-shadow:0_1px_1px_rgba(0,0,0,0.45)]",
        past && "opacity-40 saturate-50",
        startsHere && "ml-1.5 rounded-l-lg",
        endsHere && "mr-1.5 rounded-r-lg",
      )}
    >
      {startsHere && (
        <span className="truncate">
          {label}
          {booking.needs_enrichment ? " ⚠" : ""}
        </span>
      )}
    </Link>
  )
}

// One lane per property per week: the booking bars (stacked when they
// overlap) with the clean strips beneath. Every week gives the lane the same
// rows (its busiest week in the month), so filters and paging never reflow.
function PropertyLane({ property, bookings, cleans, showCleans, weekStart, rows, cleanRows, hidden }) {
  const segments = hidden ? [] : weekSegments(bookings.filter((b) => b.property_id === property.id), weekStart)
  const strips = hidden || !showCleans ? [] : cleanSegments(cleans.filter((c) => c.property_id === property.id), weekStart)

  return (
    <div className="mb-1">
      <div className="grid" style={{ gridTemplateColumns: `repeat(${HALF_COLS}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, 2rem)` }}>
        {segments.map((segment) => (
          <BookingBar key={segment.booking.id} segment={segment} property={property} />
        ))}
      </div>
      {cleanRows > 0 && (
        <div className="mt-0.5 grid gap-y-0.5" style={{ gridTemplateColumns: `repeat(${HALF_COLS}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${cleanRows}, 1.25rem)` }}>
          {strips.map((segment) => (
            <CleanStrip key={segment.clean.id} segment={segment} />
          ))}
        </div>
      )}
    </div>
  )
}

// How many stacked rows a property's bars and clean strips need in its
// busiest week of this grid.
function rowsNeeded(property, bookings, cleans, weeks) {
  const mine = bookings.filter((b) => b.property_id === property.id)
  const myCleans = cleans.filter((c) => c.property_id === property.id)
  const maxRows = (fn) => Math.max(0, ...weeks.map((weekStart) => fn(weekStart).reduce((max, seg) => Math.max(max, seg.row + 1), 0)))
  return {
    bars: Math.max(1, maxRows((w) => weekSegments(mine, w))),
    cleans: maxRows((w) => cleanSegments(myCleans, w)),
  }
}

export default function MonthGrid({ month, properties, bookings, cleans = [], notes = [], hiddenProperties = [], showCleans = true, onAddNote, onDeleteNote }) {
  const propertyById = Object.fromEntries(properties.map((p) => [ p.id, p ]))
  const today = startOfDay(new Date())
  const gridStart = startOfWeek(startOfMonth(month), WEEK_OPTS)
  const gridEnd = endOfWeek(endOfMonth(month), WEEK_OPTS)
  const weeks = []
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 7)) weeks.push(d)
  const laneRows = Object.fromEntries(properties.map((p) => [ p.id, rowsNeeded(p, bookings, cleans, weeks) ]))

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="grid grid-cols-7 border-b border-border bg-muted/60">
        {WEEKDAYS.map((day) => (
          <div key={day} className={cn("px-2 py-2 text-center text-xs font-semibold", day === "Sat" || day === "Sun" ? "bg-stone-500/[0.08] text-foreground/80 dark:bg-white/[0.04]" : "text-muted-foreground")}>
            {day}
          </div>
        ))}
      </div>

      {weeks.map((weekStart) => {
        const days = eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) })

        return (
          <div key={weekStart.toISOString()} className="relative border-b border-border last:border-b-0">
            {/* Column wash behind the whole week: days gone by fade back, today stands forward. */}
            <div aria-hidden className="pointer-events-none absolute inset-0 grid grid-cols-7">
              {days.map((day) => (
                <div
                  key={day.toISOString()}
                  className={cn(
                    // Weekends sit on a slightly deeper ground so Sat/Sun read at a glance.
                    isWeekend(day) && "bg-stone-500/[0.08] dark:bg-white/[0.04]",
                    isBefore(day, today) && "bg-muted/70 dark:bg-muted/40",
                    isWeekend(day) && isBefore(day, today) && "bg-stone-500/20 dark:bg-muted/60",
                    isToday(day) && "bg-brand/10 shadow-[inset_0_0_0_2px_var(--brand)]",
                  )}
                />
              ))}
            </div>

            {/* Date numbers get their own row -- nothing draws over them. */}
            <div className="relative grid grid-cols-7">
              {days.map((day) => (
                <div
                  key={day.toISOString()}
                  className={cn(
                    "border-r border-border/50 px-2 pt-1.5 pb-1 text-right text-sm font-medium last:border-r-0",
                    isBefore(day, today) ? "text-muted-foreground/40" : isSameMonth(day, month) ? "text-foreground/80" : "text-muted-foreground/50",
                  )}
                >
                  <span className="group/day flex items-center justify-end gap-1">
                    {onAddNote && (
                      <button
                        type="button"
                        onClick={() => onAddNote(day)}
                        title="Add a note for this day"
                        aria-label={`Add a note for ${format(day, "d MMM")}`}
                        className="mr-auto rounded p-0.5 text-muted-foreground/0 hover:bg-muted hover:text-muted-foreground group-hover/day:text-muted-foreground/60"
                      >
                        <Plus size={12} />
                      </button>
                    )}
                    {isToday(day) && <span className="text-[10px] font-bold uppercase tracking-wide text-brand">Today</span>}
                    <span
                      className={cn(
                        isToday(day) && "inline-flex size-6 items-center justify-center rounded-full bg-brand font-bold text-brand-foreground",
                      )}
                    >
                      {format(day, "d")}
                    </span>
                  </span>
                  {notes.filter((n) => n.on_date === format(day, "yyyy-MM-dd")).map((note) => (
                    <button
                      key={note.id}
                      type="button"
                      onClick={() => onDeleteNote?.(note)}
                      title={`${note.property_short_name}: ${note.body} (click to remove)`}
                      className="mt-0.5 flex w-full items-center gap-1 truncate rounded border border-amber-300 bg-amber-100 px-1 py-0.5 text-left text-[10px] font-medium leading-none text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100"
                    >
                      <span className="size-1.5 shrink-0 rounded-full" style={{ background: propertyColor(propertyById[note.property_id]) }} />
                      <StickyNote size={9} className="shrink-0" />
                      <span className="truncate">{note.body}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>

            <div className="relative">
              {properties.map((property) => (
                <PropertyLane
                  key={property.id}
                  property={property}
                  bookings={bookings}
                  cleans={cleans}
                  showCleans={showCleans}
                  weekStart={weekStart}
                  rows={laneRows[property.id].bars}
                  cleanRows={showCleans ? laneRows[property.id].cleans : 0}
                  hidden={hiddenProperties.includes(property.id)}
                />
              ))}
            </div>
            <div className="h-1.5" />
          </div>
        )
      })}
    </div>
  )
}
